import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Bell, BellOff, CheckCheck, AlertTriangle, 
  XCircle, Info, Loader2, Package, Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAlertsPanel = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchAlerts = useCallback(async () => {
    try {
      const [alertsRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/alerts`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/admin/alerts/unread-count`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (countRes.ok) {
        const data = await countRes.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error('Error fetching alerts:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const markAsRead = async (alertId) => {
    try {
      await fetch(`${API_URL}/api/admin/alerts/${alertId}/read`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAlerts();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/admin/alerts/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(language === 'pt' ? 'Todos os alertas lidos' : 'All alerts marked as read');
      fetchAlerts();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical': return <XCircle className="text-red-500" size={18} />;
      case 'warning': return <AlertTriangle className="text-yellow-500" size={18} />;
      case 'info': return <Info className="text-blue-500" size={18} />;
      default: return <Bell className="text-[#9FA2B4]" size={18} />;
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical': return 'border-red-200 bg-red-50/50';
      case 'warning': return 'border-yellow-200 bg-yellow-50/50';
      case 'info': return 'border-blue-200 bg-blue-50/50';
      default: return 'border-[#004E64]/10 bg-white';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'cancelled_order': language === 'pt' ? 'Pedido Cancelado' : 'Cancelled Order',
      'new_order': language === 'pt' ? 'Novo Pedido' : 'New Order',
      'payment_failed': language === 'pt' ? 'Pagamento Falhou' : 'Payment Failed',
      'driver_issue': language === 'pt' ? 'Problema Motorista' : 'Driver Issue',
      'system': language === 'pt' ? 'Sistema' : 'System',
    };
    return labels[type] || type;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return language === 'pt' ? 'agora' : 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  const filteredAlerts = alerts.filter(a => {
    if (filter === 'unread') return !a.is_read;
    if (filter === 'critical') return a.severity === 'critical';
    return true;
  });

  return (
    <Card className="rounded-2xl" data-testid="admin-alerts-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Bell className="text-[#FF6B35]" size={20} />
            {language === 'pt' ? 'Alertas do Sistema' : 'System Alerts'}
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs ml-1">{unreadCount}</Badge>
            )}
          </span>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={markAllAsRead} className="text-xs rounded-full" data-testid="mark-all-read-btn">
                <CheckCheck size={14} className="mr-1" />
                {language === 'pt' ? 'Marcar Todos Lidos' : 'Mark All Read'}
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-2 mb-4">
          {['all', 'unread', 'critical'].map(f => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={`text-xs rounded-full ${filter === f ? 'bg-[#FF6B35] hover:bg-[#E85D2A]' : ''}`}
              data-testid={`filter-${f}`}
            >
              {f === 'all' && (language === 'pt' ? 'Todos' : 'All')}
              {f === 'unread' && (language === 'pt' ? `Não Lidos (${unreadCount})` : `Unread (${unreadCount})`)}
              {f === 'critical' && (language === 'pt' ? 'Críticos' : 'Critical')}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#FF6B35]" /></div>
        ) : filteredAlerts.length === 0 ? (
          <div className="text-center py-12">
            <BellOff size={40} className="mx-auto text-[#9FA2B4] mb-3" />
            <p className="text-[#9FA2B4] text-sm">
              {language === 'pt' ? 'Nenhum alerta encontrado' : 'No alerts found'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {filteredAlerts.map(alert => (
              <div
                key={alert.alert_id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                  alert.is_read ? 'opacity-60 bg-gray-50 border-gray-100' : getSeverityBg(alert.severity)
                }`}
                data-testid={`alert-${alert.alert_id}`}
              >
                <div className="mt-0.5 flex-shrink-0">{getSeverityIcon(alert.severity)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-semibold text-sm text-[#004E64]">{alert.title}</span>
                    <Badge className="text-[10px] bg-[#004E64]/10 text-[#004E64]">{getTypeLabel(alert.alert_type)}</Badge>
                    {!alert.is_read && <span className="w-2 h-2 rounded-full bg-[#FF6B35]" />}
                  </div>
                  <p className="text-xs text-[#9FA2B4] line-clamp-2">{alert.message}</p>
                  {alert.data?.order_id && (
                    <p className="text-xs text-[#FF6B35] mt-1 flex items-center gap-1">
                      <Package size={10} /> #{alert.data.order_id.slice(-8)}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] text-[#9FA2B4]">{formatTime(alert.created_at)}</span>
                  {!alert.is_read && (
                    <Button variant="ghost" size="sm" onClick={() => markAsRead(alert.alert_id)} className="h-6 px-2 text-[10px]">
                      {language === 'pt' ? 'Lido' : 'Read'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAlertsPanel;
